import { InformationCircleIcon, WalletIcon } from '@heroicons/react/24/outline';
import { QueryStatus } from '@tanstack/react-query';
import { useFormatter } from 'use-intl';
import { BaseError, Chain, TransactionReceipt, formatUnits } from 'viem';
import Button from '../../elements/Button';
import { Dispatch, SetStateAction } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { getChainConstant } from '@/domain/chain';
import { Addresses } from '@/domain/addresses';

export interface MigrateProps {
  chain: Chain;
  balanceStatus: QueryStatus;
  balanceFloor?: bigint;
  balanceSFloor?: bigint;
  balanceAFloor?: bigint;
  balanceGFloor?: bigint;
  balanceFloorFromGFloor?: bigint;
  gfloorToFloorRatio?: bigint;
  onMigrateClick: () => void;
  floorToReceive?: bigint;
  error: Error | null;
  setError: Dispatch<SetStateAction<Error | null>>;
  canMigrate: boolean;
  isPending: boolean;
  isConfirming: boolean;
  isConfirmed: boolean;
  alreadyClaimed: boolean;
  receipt?: TransactionReceipt;
  watchAsset: () => void;
}

const Migrate = ({
  chain,
  balanceStatus,
  balanceAFloor,
  balanceFloor,
  balanceGFloor,
  balanceSFloor,
  balanceFloorFromGFloor,
  gfloorToFloorRatio,
  onMigrateClick,
  floorToReceive,
  isConfirming,
  isConfirmed,
  alreadyClaimed,
  error,
  setError,
  canMigrate,
  receipt,
  watchAsset,
}: MigrateProps) => {
  const { number } = useFormatter();

  // users who have claimed previously but not within this current active tx
  if (alreadyClaimed && !isConfirmed) {
    return (
      <div className="rounded-xl bg-gray-800 col-span-1 lg:col-span-6 flex flex-col">
        <h1 className="border-b border-gray-400 px-6  py-5 text-xl text-center">
          {'Migrate FLOOR to V2'}
        </h1>
        <section className="p-6 relative flex-1">
          <div className="text-center space-y-4">
            <p>
              <CheckCircleIcon className="h-8 w-8 text-white inline-block" />
            </p>
            <p className="text-base">{`This address has already claimed.`}</p>
            <p className="text-sm text-center">
              <button
                type="button"
                className="underline hover:no-underline text-xs text-gray-200"
                onClick={watchAsset}
              >
                {'Add FLOOR to wallet'}
              </button>
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-gray-800 col-span-1 lg:col-span-6 flex flex-col">
      <h1 className="border-b border-gray-400 px-6  py-5 text-xl text-center">
        {'Migrate FLOOR to V2'}
      </h1>
      {balanceStatus === 'pending' && (
        <section className="p-6 relative flex-1">
          <div className="text-center space-y-2">
            <WalletIcon className="h-8 w-8 animate-bounce text-gray-500 inline-block" />
            <p className="text-sm">{'Checking wallet balances...'}</p>
          </div>
        </section>
      )}
      {balanceStatus === 'success' && !floorToReceive && !isConfirmed && (
        <section className="p-6 relative flex-1">
          <div className="text-center">
            <p className="text-sm">{'No FLOOR to migrate'}</p>
          </div>
        </section>
      )}
      {balanceStatus === 'success' && !!floorToReceive && (
        <>
          {!isConfirmed && (
            <section className="p-6 relative flex-1 border-b border-gray-400">
              <dl className="space-y-4">
                {!!balanceFloor && (
                  <div className="flex justify-between items-center">
                    <dt>{'FLOOR balance'}</dt>
                    <dd className="text-right flex justify-end space-x-4 items-center">
                      <span>
                        {number(Number(formatUnits(balanceFloor, 9)), {
                          minimumSignificantDigits: 1,
                        })}
                      </span>
                    </dd>
                  </div>
                )}
                {!!balanceSFloor && (
                  <div className="flex justify-between items-center">
                    <dt>{'sFLOOR balance'}</dt>
                    <dd className="text-right flex justify-end space-x-4 items-center">
                      <span>
                        {number(Number(formatUnits(balanceSFloor, 9)))}
                      </span>
                    </dd>
                  </div>
                )}
                {!!balanceAFloor && (
                  <div className="flex justify-between items-center">
                    <dt>{'aFLOOR balance'}</dt>
                    <dd className="text-right flex justify-end space-x-4 items-center">
                      <span>
                        {number(Number(formatUnits(balanceAFloor, 9)), {
                          minimumSignificantDigits: 1,
                        })}
                      </span>
                    </dd>
                  </div>
                )}
                {!!balanceGFloor && (
                  <div className="flex justify-between items-center">
                    <dt>
                      {'gFLOOR balance'}
                      {!!gfloorToFloorRatio && (
                        <div className="text-sm text-gray-200">
                          {'1 gFLOOR = approx. '}
                          {number(Number(formatUnits(gfloorToFloorRatio, 9)), {
                            maximumFractionDigits: 2,
                          })}
                          {' FLOOR'}
                        </div>
                      )}
                    </dt>
                    <dd className="text-right flex justify-end space-x-4 items-center">
                      <div>
                        {number(Number(formatUnits(balanceGFloor, 18)))}
                        {!!balanceFloorFromGFloor && (
                          <div className="text-sm text-gray-200">
                            {'~'}
                            {number(
                              Number(formatUnits(balanceFloorFromGFloor, 9)),
                              {
                                maximumFractionDigits: 0,
                              },
                            )}
                            {' FLOOR'}
                          </div>
                        )}
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          )}
          <div className="px-6 py-5">
            {isConfirmed ? (
              <div className="text-center space-y-4">
                <p>
                  <CheckCircleIcon className="h-8 w-8 text-white inline-block" />
                </p>
                <p className="text-base">{'Claim successful.'}</p>
                <p className="text-sm space-x-4">
                  <a
                    href={`${chain.blockExplorers?.default.url}/tx/${receipt?.transactionHash}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline hover:no-underline text-xs text-gray-200"
                  >
                    {`View on ${chain.blockExplorers?.default.name}`}
                  </a>
                  <button
                    type="button"
                    className="underline hover:no-underline text-xs text-gray-200"
                    onClick={watchAsset}
                  >
                    {'Add FLOOR to wallet'}
                  </button>
                </p>
              </div>
            ) : (
              <>
                <dl className="space-y-4">
                  <div className="flex justify-between items-center text-xl">
                    <dt>{'FLOOR to receive'}</dt>
                    <dd className="text-right">
                      <span>
                        {number(Number(formatUnits(floorToReceive, 18)), {
                          minimumSignificantDigits: 1,
                        })}
                      </span>
                    </dd>
                  </div>
                  <div className="flex justify-between items-center text-base">
                    <dt>{'Status'}</dt>
                    <dd className="text-right">
                      <span>
                        {canMigrate ? 'Ready to claim' : `Not ready to claim`}
                      </span>
                    </dd>
                  </div>
                </dl>
                <div className="p-6 flex justify-center items-center">
                  <Button
                    variant="primary"
                    onClick={onMigrateClick}
                    disabled={!canMigrate}
                    processing={isConfirming}
                  >
                    {'Claim V2 FLOOR'}
                  </Button>
                </div>
                <aside className="p-2 text-xs bg-brand-blue/10 rounded-md flex items-center">
                  <InformationCircleIcon className="w-5 h-5 flex-none mr-2" />
                  {`Migration claim contract: `}
                  <a
                    href={`${
                      chain.blockExplorers?.default.url
                    }/address/${getChainConstant(
                      Addresses,
                      chain.id,
                      'MigrateFloorToken',
                    )}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline hover:no-underline text-xs text-gray-50 mx-1 truncate"
                  >
                    {getChainConstant(
                      Addresses,
                      chain.id,
                      'FloorV1TokenMerkleClaim',
                    )}
                  </a>
                </aside>
              </>
            )}

            {error &&
              !(error as BaseError)?.details?.includes(
                'User rejected the request',
              ) && (
                <div className="bg-red-500 text-white p-4 rounded-md text-xs mt-4">
                  <h4 className="font-bold">{error?.name}</h4>
                  <pre className="text-xs overflow-x-scroll">
                    {error?.message}
                  </pre>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );
};

export default Migrate;
